/**
 * @fileoverview Capture Test Suite
 *
 * Tests for piece captures including special capture rules.
 * Each piece captures a different opponent piece to demonstrate capture mechanics.
 */

import { PieceType } from "../../zodiacEngine/types";
import { IntegrationTest, TestSuite } from "../types";

export const captureTests: IntegrationTest[] = [
  // ============ BEAR ============
  // Bear's "Savage" ability - can capture ANY piece (including Large pieces)
  {
    name: "Bear captures Rhino",
    description: "Light Bear uses Savage to capture Dark Rhino",
    inputFEN: "8/8/BK7/1r6/7bk",
    lightKing: PieceType.Bear,
    darkKing: PieceType.Bear,
    move: "Light Bear C1->D2",
    expectedFEN: "8/8/8/1BK6/7bk",
    expectedDelta: {
      pieceMoved: "Light Bear captured Dark Rhino [C1 -> D2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ CRANE ============
  // Crane CANNOT capture Large pieces (Bear/Rhino/Tree/Wolf)
  {
    name: "Crane captures Goat",
    description: "Light Crane captures Dark Goat",
    inputFEN: "8/8/CK7/1g6/7bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Crane C1->D2",
    expectedFEN: "8/8/8/1CK6/7bk",
    expectedDelta: {
      pieceMoved: "Light Crane captured Dark Goat [C1 -> D2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ FROG ============
  // Frog captures Snake piece
  {
    name: "Frog captures Snake",
    description: "Light Frog captures Dark Snake",
    inputFEN: "8/8/1FK6/8/3s*3bk",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog C2->E4",
    expectedFEN: "8/8/8/8/3FK3bk",
    expectedDelta: {
      pieceMoved: "Light Frog captured Dark Snake [C2 -> E4]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // Frog captures via wall climb
    {
      name: "Frog wallclimb captures Crane",
      description: "Light Frog captures Dark Snake",
      inputFEN: "1FKc5/8/8/8/7bk",
      lightKing: PieceType.Frog,
      darkKing: PieceType.Bear,
      move: "Light Frog A2->A3",
      expectedFEN: "2FK5/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Frog captured Dark Crane [A2 -> A3]",
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
    },

        // Frog captures via wallblock
        {
          name: "Frog wallblock captures Crane",
          description: "Light Frog captures Dark Snake",
          inputFEN: "2c5/1FK6/8/8/7bk",
          lightKing: PieceType.Frog,
          darkKing: PieceType.Bear,
          move: "Light Frog B2->A3",
          expectedFEN: "2FK5/8/8/8/7bk",
          expectedDelta: {
            pieceMoved: "Light Frog captured Dark Crane [B2 -> A3]",
            turnChanged: true,
            bonusTurn: false,
            gameEnded: false,
          },
        },

                // Frog captures via lilyjump
        {
          name: "Frog Lilyjump captures Crane",
          description: "Light Frog captures Dark Snake",
          inputFEN: "2c5/8/8/F*K7/7bk",
          lightKing: PieceType.Frog,
          darkKing: PieceType.Bear,
          move: "Light Frog D1->A3",
          expectedFEN: "2FK5/8/8/8/7bk",
          expectedDelta: {
            pieceMoved: "Light Frog jumped off of its Lilypad and captured Dark Crane [D1 -> A3]",
            turnChanged: true,
            bonusTurn: false,
            gameEnded: false,
          },
        },

  // ============ FUNGI ============
  // Fungi captures and "Zombifies" the piece (switches teams for 1 turn)
  // NOTE: darkKing must match the FEN king marker (ck = Crane is king, not Bear)
  {
    name: "Fungi captures Bear",
    description: "Light Fungi captures Dark Bear",
    inputFEN: "8/8/UK7/1b6/7ck",
    lightKing: PieceType.Fungi,
    darkKing: PieceType.Crane,
    move: "Light Fungi C1->D2",
    expectedFEN: "8/8/8/1UK6/7ck",
    expectedDelta: {
      pieceMoved: "Light Fungi captured Dark Bear [C1 -> D2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ GOAT ============
  // Goat captures and "Faints" when capturing predators (Bear/Wolf)
  {
    name: "Goat captures Crane",
    description: "Light Goat captures Dark Crane",
    inputFEN: "8/8/GK7/1c6/7bk",
    lightKing: PieceType.Goat,
    darkKing: PieceType.Bear,
    move: "Light Goat C1->D2",
    expectedFEN: "8/8/8/1GK6/7b♕",
    expectedDelta: {
      pieceMoved: "Light Goat captured Dark Crane [C1 -> D2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ MONKEY ============
  // Monkey can "Consume" Fungi
  {
    name: "Monkey captures Goat",
    description: "Light Monkey captures Dark Goat",
    inputFEN: "8/8/MK7/1g6/7bk",
    lightKing: PieceType.Monkey,
    darkKing: PieceType.Bear,
    move: "Light Monkey C1->D2",
    expectedFEN: "8/8/8/1MK6/7bk",
    expectedDelta: {
      pieceMoved: "Light Monkey captured Dark Goat [C1 -> D2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ OCTOPUS ============
  // Octopus can only capture orthogonally (not diagonally - diagonal entangles)
  {
    name: "Octopus captures Crane",
    description: "Light Octopus captures Dark Crane orthogonally",
    inputFEN: "8/8/OKc6/8/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->C2",
    expectedFEN: "8/8/1OK6/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Octopus captured Dark Crane [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },
    // ============ RHINO ============
  // Rhino "Tramples" everything in its path
  {
    name: "Rhino captures Tree",
    description: "Light Rhino captures Dark Tree",
    inputFEN: "8/8/1R*K6/t7/7bk",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C2->D1",
    expectedFEN: "8/8/8/R*K7/7bk",
    expectedDelta: {
      pieceMoved: "Light Rhino captured Dark Tree [C2 -> D1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },
  // ============ RHINO ============
  // Rhino "Tramples" everything in its path
  {
    name: "Rhino tramples Monkey and Goat",
    description: "Light Rhino tramples Dark Monkey and Dark Goat",
    inputFEN: "8/8/RKmg5/8/7bk",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C1->C3",
    expectedFEN: "8/8/2RK5/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Rhino trampled Dark Monkey [C2] and Dark Goat [C3] [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // Rhino "Tramples" everything in its path
    {
      name: "Rhino charges Monkey and Goat and Crane",
      description: "Light Rhino tramples Dark Monkey and Dark Goat and Dark Crane",
      inputFEN: "8/8/RKmgc4/8/7bk",
      lightKing: PieceType.Rhino,
      darkKing: PieceType.Bear,
      move: "Light Rhino C1->C4",
      expectedFEN: "8/8/3RK4/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Rhino trampled Dark Monkey [C2], Dark Goat [C3] and Dark Crane [C4] [C1 -> C4]",
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
    },

  // ============ SNAKE ============
  // Snake standard capture
  {
    name: "Snake captures Octopus",
    description: "Light Snake captures Dark Octopus",
    inputFEN: "8/8/S*K1o5/8/7bk",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Bear,
    move: "Light Snake C1->C3",
    expectedFEN: "8/8/2S*K5/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Snake captured Dark Octopus [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // Snake strike capture
    {
      name: "Snake strike captures Octopus",
      description: "Light Snake snake strikes Dark Octopus",
      inputFEN: "8/8/S*K2o4/8/7bk",
      lightKing: PieceType.Snake,
      darkKing: PieceType.Bear,
      move: "Light Snake C1->C4",
      expectedFEN: "8/8/3S*K4/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Snake captured Dark Octopus [C1 -> C4]",
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
    },

  // ============ TREE ============
  // Tree can "Crush" fainted goats
  {
    name: "Tree captures Penguin",
    description: "Light Tree captures Dark Penguin",
    inputFEN: "8/8/TKp↑6/8/7bk",
    lightKing: PieceType.Tree,
    darkKing: PieceType.Bear,
    move: "Light Tree C1->C2",
    expectedFEN: "8/8/1TK6/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Tree captured Dark Penguin [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ VIRUS ============
  // Virus "infects" pieces and captures when leaving
  // Note: No bonus turn because there are no valid infection targets from A3
  {
    name: "Virus infects then captures",
    description: "Light Virus captures dark crane by leaving infection",
    inputFEN: "2CK5/8/2V+c5/8/7bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Virus C3->A3:infect",
    expectedFEN: "2CK+V5/8/8/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Virus infected Light Crane [C3 -> A3] Light Virus captured Dark Crane [C3]",
      turnChanged: true, // No bonus turn - no valid targets from A3
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ WOLF ============
  // Wolf standard capture
  {
    name: "Wolf captures Tree",
    description: "Light Wolf captures Dark Tree",
    inputFEN: "M7/8/WK7/2t5/7bk",
    lightKing: PieceType.Wolf,
    darkKing: PieceType.Bear,
    move: "Light Wolf C1->D3",
    expectedFEN: "M7/8/8/2WK5/7bk",
    expectedDelta: {
      pieceMoved: "Light Wolf captured Dark Tree [C1 -> D3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // Lone Wolf special capture
    {
      name: "Lone Wolf captures Tree",
      description: "Light Wolf captures Dark Tree",
      inputFEN: "8/8/WK7/3t4/7bk",
      lightKing: PieceType.Wolf,
      darkKing: PieceType.Bear,
      move: "Light Wolf C1->D4",
      expectedFEN: "8/8/8/3WK4/7bk",
      expectedDelta: {
        pieceMoved: "Light Wolf captured Dark Tree [C1 -> D4]",
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
    },
];

export const capturesSuite: TestSuite = {
  name: "Captures",
  description: "Tests for piece captures - one capture test per piece type (13 tests)",
  tests: captureTests,
};
